import mediaQuery from 'css-mediaquery';
import {getBowserState} from '@epic-core/common';
import {twinColors} from './twinmotion/twinCommonThemeConfig';

/**
 * Bootstrap breakpoints
value         |0px     576px    768px    992px   1200px   1440px  1920px     2560px
key           |xs      sm       md       lg       xl       xxl      hd         qhd
screen width  |--------|--------|--------|--------|---------|--------|----------|------------->
range         |   xs   |   sm   |   md   |   lg   |   xl    |   xxl  |     hd   |     qhd
*/
export const breakpoints = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1440,
    hd: 1920,
    qhd: 2560
};

/**
 * Material UI breakpoints
value         |0px     600px    960px    1280px   1920px  2560px
key           |xs      sm       md       lg       xl      qhd
screen width  |--------|--------|--------|--------|--------|------------->
range         |   xs   |   sm   |   md   |   lg   |   xl   |     qhd
*/
export const breakpointsMatui = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
    qhd: 2560
};

type Generic = {[key: string]: any} | string | number | undefined;

export const getDescendantThemeProp = (obj: Generic, path: string): Generic => {
    const arr = path.split(/[.[]['"]?/) || [];
    let o = obj;
    while (arr.length && o) {
        const shifted = arr.shift() || '';
        o = o[shifted.replace(/['"]?]$/, '')];
    }
    return o;
};

export const ueFonts = {
    opensans: 'OpenSans, sans-serif, arial',
    brutal: {
        bold: 'Brutal_Bold, Open Sans, arial',
        medium: 'Brutal_Medium, Open Sans, arial',
        regular: 'Brutal_Regular, Open Sans, arial',
        light: 'Brutal_Light, Open Sans, arial',
        extralight: 'Brutal_ExtraLight, Open Sans, arial'
    },
    icomoon: 'ue-icomoon',
    fira: {
        bold: 'Fira_Mono_Bold, Brutal_Bold, monospace',
        medium: 'Fira_Mono_Medium, Brutal_Medium, monospace',
        regular: 'Fira_Mono_Regular, Brutal_Light, monospace'
    },
    notosans: {
        japanese: 'Noto Sans JP, Noto-Regular, sans-serif',
        korean: 'Noto Sans KR, Noto-Regular, sans-serif',
        chinese: 'Noto Sans SC, Noto-Regular, sans-serif'
    }
};

export const ueAccentColors = {
    dark: {
        sky: '#0057D9',
        forest: '#00705C',
        galaxy: '#9824E0',
        fire: '#CB1233',
        orchid: '#D01073',
        neon: '#5BBE65'
    },
    bright: {
        sky: '#00ABF5',
        forest: '#2AE5B3',
        galaxy: '#B665E9',
        fire: '#FF5F68',
        orchid: '#F43C9C',
        neon: '#8BE0AE'
    }
};

export const ueNeutralColors = {
    stone: '#DBDEE3',
    chalk: '#ECEFF2',
    pearl: '#F7FAFD',
    charcoal: '#0E1128',
    concrete: '#202337',
    steel: '#383B50',
    silver: '#A6A7AE'
};

export const ueGradientColors = {
    sky: {
        from: '#34C3FF',
        to: '#338EEE'
    },
    forest: {
        from: '#19F4BD',
        to: '#10CFCB'
    },
    fire: {
        from: '#FF7C77',
        to: '#FF5470'
    },
    galaxy: {
        from: '#C776F9',
        to: '#C776F9'
    },
    orchid: {
        from: '#F43C9C',
        to: '#D01073'
    },
    black: {
        from: '#000',
        to: '#000'
    },
    white: {
        from: '#fff',
        to: '#fff'
    },
    neon: {
        from: '#65de8a',
        to: '#24975d'
    }
};

export const ueMain = '#009DDF';

export const easing = 'cubic-bezier(0, 0.42, 0.2, 0.99)';

const ssrMatchMedia = (query: string): {matches: any} => {
    const {mobile, tablet} = getBowserState();
    let width = breakpointsMatui.xl;
    if (tablet) {
        width = breakpointsMatui.md;
    } else if (mobile) {
        width = breakpointsMatui.sm;
    }

    return {
        matches: mediaQuery.match(query, {width})
    };
};

export const ueCommonTheme = {
    spacing: 8,
    easing,
    palette: {
        primary: {
            main: ueMain
        },
        accent: {
            ...ueAccentColors
        },
        neutral: {
            ...ueNeutralColors,
            ...twinColors.dark,
            ...twinColors.light
        },
        gradient: {
            ...ueGradientColors
        }
    },
    widths: {
        article: 640
    },
    breakpoints: {
        values: {
            ...breakpointsMatui
        }
    },
    ifLight: (p: {[key: string]: any}, lightKey: string, darkKey: string): string => {
        let prop;
        if (p.theme.palette.type === 'light') {
            if (lightKey.indexOf('theme.') === 0) {
                prop = getDescendantThemeProp(p, lightKey);
                return prop;
            }
            return lightKey;
        }
        if (lightKey.indexOf('theme.') === 0) {
            prop = getDescendantThemeProp(p, darkKey);
            return prop;
        }
        return darkKey;
    },
    ue: {
        fonts: ueFonts
    },
    fonts: ueFonts,
    props: {
        // Change the default options of useMediaQuery
        MuiUseMediaQuery: {ssrMatchMedia}
    }
};

export const breakpointsInPx = Object.keys(breakpoints).reduce((acc, bp) => {
    acc[bp] = `${breakpoints[bp]}px`;
    return acc;
}, {});
